import * as React from "react";
import type { NaverMapsNamespace } from "./types";

// SDK는 ERB 레이아웃의 <script defer>로 로드된다. defer는 HTML 파싱 이후
// 순서대로 실행되므로 React 마운트 시점에 이미 ready일 수도, 아직 아닐 수도 있다.
// 준비 감지는 (1) 이미 ready면 즉시, (2) 주입된 <script>의 load/error 이벤트로
// "로드되는 순간" 결정적으로 resolve, (3) 이벤트를 놓치는 경우(스크립트가 리스너
// 부착 전 이미 실행 완료 등)를 위한 빠른 백스톱 폴링 순으로 처리한다. 과거 200ms
// 폴링은 SDK가 준비돼도 최대 200ms를 흘려보냈다 — 이벤트화로 그 지연을 제거한다.
// 모듈 레벨 Promise로 캐시해 다중 마운트 시 중복 작업을 막는다.
const POLL_INTERVAL_MS = 30;   // 백스톱 폴링 간격(이벤트 기반이라 보조 역할).
const POLL_MAX_MS = 10_000;    // 전체 타임아웃(기존 10s 유지).
const SDK_SELECTOR = 'script[src*="oapi.map.naver.com/openapi/v3/maps.js"]';
const SDK_TIMEOUT_ERROR = "naver-maps-load-timeout";
const SDK_MISSING_ERROR = "naver-maps-script-missing";

let cachedReady: Promise<NaverMapsNamespace> | null = null;

// 외부에서 직접 호출할 수도 있게 export. 훅 외에 마커 모듈 등에서 ready를
// 보장해야 할 때 사용한다.
export function loadNaverMaps(): Promise<NaverMapsNamespace> {
  if (cachedReady) return cachedReady;

  cachedReady = new Promise((resolve, reject) => {
    if (window.naver?.maps) return resolve(window.naver);

    let settled = false;
    let timer = 0;
    const script = document.querySelector(SDK_SELECTOR) as HTMLScriptElement | null;

    // SDK <script>는 서버(design/show.html.erb)가 화면 화이트리스트로만 심는다.
    // 태그 자체가 없다면 이 화면이 목록에서 빠졌거나 키가 미주입된 것 — 아무리
    // 기다려도 로드될 리 없으므로 10초 폴링을 돌지 않고 즉시 실패시킨다.
    // (모달 안에서 처음 드러나는 LocationPicker 가 10초간 "불러오는 중…"에
    //  머무는 대신, 원인이 콘솔과 화면 오류로 바로 보이게 한다.)
    if (!script) {
      cachedReady = null;
      // eslint-disable-next-line no-console
      console.warn(
        "[naver-maps] SDK script tag not found. " +
        "app/views/design/show.html.erb 의 지도 화면 목록에 현재 screen 을 추가했는지, " +
        "NCP 키(naver_map_key_id)가 주입됐는지 확인하세요.",
      );
      return reject(new Error(SDK_MISSING_ERROR));
    }

    const cleanup = () => {
      window.clearTimeout(timer);
      if (script) {
        script.removeEventListener("load", onLoad);
        script.removeEventListener("error", onError);
      }
    };
    const succeed = () => {
      if (settled) return;
      settled = true; cleanup(); resolve(window.naver!);
    };
    const fail = () => {
      if (settled) return;
      settled = true; cleanup();
      // 실패 캐시는 비워 두어 운영자가 script 태그를 수정 후 새로고침 없이 다시
      // 시도할 수 있게 한다(개발 환경 편의).
      cachedReady = null;
      reject(new Error(SDK_TIMEOUT_ERROR));
    };

    // (2) 스크립트 load/error — 로드되는 순간 결정적으로 처리.
    const onLoad = () => { window.naver?.maps ? succeed() : fail(); };
    const onError = () => fail();
    if (script) {
      script.addEventListener("load", onLoad);
      script.addEventListener("error", onError);
    }

    // (3) 백스톱 폴링 — load 이벤트를 놓친 경우(이미 실행 완료 등)와 인증 실패 후
    // window.naver.maps가 잠깐 떴다 사라지는 케이스를 30ms 간격으로 빠르게 포착.
    let elapsed = 0;
    const tick = () => {
      if (settled) return;
      if (window.naver?.maps) return succeed();
      elapsed += POLL_INTERVAL_MS;
      if (elapsed >= POLL_MAX_MS) return fail();
      timer = window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    timer = window.setTimeout(tick, POLL_INTERVAL_MS);
  });

  return cachedReady;
}

export type NaverMapsLoadState = "loading" | "ready" | "error";

// React 컴포넌트가 SDK 준비 상태를 구독하는 표준 진입점.
// 'ready'가 떨어진 후에야 window.naver.maps API를 만져야 한다.
export function useNaverMaps(): NaverMapsLoadState {
  const [state, setState] = React.useState<NaverMapsLoadState>(
    typeof window !== "undefined" && window.naver?.maps ? "ready" : "loading",
  );

  React.useEffect(() => {
    let cancelled = false;
    loadNaverMaps()
      .then(() => { if (!cancelled) setState("ready"); })
      .catch(() => { if (!cancelled) setState("error"); });
    return () => { cancelled = true; };
  }, []);

  return state;
}
